import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, 
  FileText, 
  MapPin, 
  Camera, 
  Users, 
  Lock,
  ArrowRight,
  Sparkles
} from 'lucide-react';

export default function Home() {
  const { appConfig, loading } = useApp();

  const features = [
    {
      icon: Camera,
      title: 'Share Stories',
      description: 'Capture and share moments with friends through photos and videos that disappear after 24 hours.'
    },
    {
      icon: MapPin,
      title: 'Location Sharing',
      description: 'Share your real-time location with trusted friends for a limited time.'
    },
    {
      icon: Users,
      title: 'Connect',
      description: 'Build your circle of friends and discover new connections.'
    },
    {
      icon: Lock,
      title: 'Privacy First',
      description: 'Block unwanted users and control who sees your content.'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-loading">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1762237855157-eb0ba25e39d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwc2VsZmllJTIwcGFydHklMjBuaWdodHxlbnwwfHx8fDE3NzQxNzk3NTh8MA&ixlib=rb-4.1.0&q=85"
            alt="Friends sharing moments"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Privacy Hub</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground">
                Welcome to{' '}
                <span className="gradient-text">{appConfig.app_name}</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                {appConfig.app_description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/privacy-policy">
                  <Button 
                    size="lg" 
                    className="rounded-full glow-primary gap-2 w-full sm:w-auto"
                    data-testid="cta-privacy-policy"
                  >
                    <Shield className="w-4 h-4" strokeWidth={1.5} />
                    Privacy Policy
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/terms-of-service">
                  <Button 
                    variant="secondary" 
                    size="lg" 
                    className="rounded-full gap-2 w-full sm:w-auto"
                    data-testid="cta-terms-of-service"
                  >
                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                    Terms of Service
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Visual - Phone Mockup */}
            <div className="hidden lg:block animate-fade-in-up animation-delay-200">
              <div className="relative flex justify-center">
                <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/20 to-blue-600/20 rounded-3xl blur-3xl" />
                {/* Phone Frame */}
                <div className="relative w-[280px] h-[560px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl border-4 border-gray-800">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-10" />
                  {/* Phone Screen */}
                  <div className="w-full h-full rounded-[2.5rem] overflow-hidden bg-white">
                    <img
                      src="https://customer-assets.emergentagent.com/job_dynamic-privacy-hub/artifacts/v08n4yx1_IMG_8380.PNG"
                      alt="Histories App Screenshot"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {/* Phone Button */}
                  <div className="absolute -right-1 top-28 w-1 h-12 bg-gray-700 rounded-r-lg" />
                  <div className="absolute -left-1 top-20 w-1 h-8 bg-gray-700 rounded-l-lg" />
                  <div className="absolute -left-1 top-32 w-1 h-12 bg-gray-700 rounded-l-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-card/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
              What {appConfig.app_name} Offers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience a new way to connect and share with the people who matter most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={feature.title}
                  className="bg-card border-border/50 hover:border-primary/50 transition-all duration-300 group"
                  data-testid={`feature-card-${index}`}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Legal Links Section */}
      <section className="py-24" data-testid="legal-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/privacy-policy" className="group" data-testid="privacy-card-link">
              <Card className="h-full bg-card border-border/50 hover:border-primary/50 transition-all duration-300 overflow-hidden">
                <CardContent className="p-8 flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                      Privacy Policy
                    </h3>
                    <p className="text-muted-foreground">
                      Learn how we collect, use, and protect your personal information. Your privacy is our priority.
                    </p>
                    <div className="flex items-center gap-2 text-primary text-sm font-medium pt-2">
                      Read More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/terms-of-service" className="group" data-testid="terms-card-link">
              <Card className="h-full bg-card border-border/50 hover:border-primary/50 transition-all duration-300 overflow-hidden">
                <CardContent className="p-8 flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20 transition-colors">
                    <FileText className="w-8 h-8 text-accent" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-foreground group-hover:text-accent transition-colors">
                      Terms of Service
                    </h3>
                    <p className="text-muted-foreground">
                      Understand the rules and guidelines for using {appConfig.app_name}. Fair use for everyone.
                    </p>
                    <div className="flex items-center gap-2 text-accent text-sm font-medium pt-2">
                      Read More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 bg-card/30" data-testid="community-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/10 to-blue-600/10 rounded-3xl blur-2xl" />
                <img
                  src="https://images.pexels.com/photos/7149137/pexels-photo-7149137.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Community"
                  className="relative rounded-2xl border border-border/50"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground">
                Join Our Community
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {appConfig.app_name} is built for people who value authentic connections. 
                Share your stories, discover new perspectives, and stay connected with 
                the people who matter most to you.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <div className="px-4 py-2 rounded-full bg-secondary text-sm text-muted-foreground">
                  Millions of Stories Shared
                </div>
                <div className="px-4 py-2 rounded-full bg-secondary text-sm text-muted-foreground">
                  Privacy Focused
                </div>
                <div className="px-4 py-2 rounded-full bg-secondary text-sm text-muted-foreground">
                  24/7 Support
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
